import { FC } from "react";
import { APIEndpoints } from "src/types/apiTypes";
import {
  ClaimQueryParamsState,
  CopayCasesTabConfig,
} from "../copayClaimsHCP/types";

export type TodoActionDashboardApiResponse = {
  code: number;
  message: string;
  data: {
    max_treatable_page: number;
    message: string;
    treatables: Treatables[] | [];
    max_todo_page: number;
    todos: Todos[] | [];
    max_not_treated_page: number;
    not_treated: NotTreated[] | [];
    max_sp_treatments_page: number;
    sp_treatments: SpTreatments[] | [];
  };
};

export type SpTreatments = {
  patient_name: string;
  patient_id: number;
  bi_case_id: number;
  provider: string;
  pharmacy_sp_status: string;
  pharmacy_sp_status_color: 1 | 2 | 3 | 4 | 5;
  provide_treatment_status:
    | ["Treated"]
    | ["Not Treated"]
    | ["Treated", "Not Treated"];
  sp_transferred_to: string | null;
  date_transferred: string | null;
  status_update: ["text-color-yellow"] | [null];
  date_shipped: string | null;
  carrier: string | null;
  tracking_number: string | null;
  sp_status: string;
  sp_substatus: string;
};

export type NotTreated = {
  patient_name: string;
  patient_id: number;
  bi_case_id: number;
  reverification: string;
  bi_submitted: string;
  bi_completed: string;
  payer: string;
  physician: string;
  medical_coverage_status: string;
  medical_provide_treatment_status:
    | ["Treated"]
    | ["Not Treated"]
    | ["Treated", "Not Treated"];
  pharmacy_coverage_status: string;
  pharmacy_provide_treatment_status: "Not Treated" | null;
};

export type Todos = {
  patient_name: string;
  patient_id: number;
  bi_case_id: number;
  reverification: string;
  bi_completed: string;
  payer: string;
  physician: string;
  medical_coverage_status: string;
  medical_next_steps: string;
  pharmacy_coverage_status: string;
  pharmacy_next_steps:
    | ["Treated"]
    | ["Not Treated"]
    | ["Treated", "Not Treated"];
};

export type Treatables = {
  patient_name: string;
  patient_id: number;
  bi_case_id: number;
  reverification: string | null;
  bi_completed: string;
  payer: string;
  physician: string;
  medical_coverage_status?: string;
  medical_provide_treatment_status?:
    | ["Treated"]
    | ["Not Treated"]
    | ["Treated", "Not Treated"];
  pharmacy_coverage_status?: string;
  pharmacy_provide_treatment_status?:
    | ["Treated"]
    | ["Not Treated"]
    | ["Treated", "Not Treated"];
};

export type TodoActionDashboardErrorResponse = {
  code: number;
  error: string;
};

export type PatientFilterState = {
  start_date: string;
  end_date: string;
  case_id: string;
  global_patient_id: string;
  global_doctor_id: string;
  practice_id: string;
  ref_user: string;
  role: string;
  id: string;
  treatable_page: number;
  todo_page: number;
  sp_treatment_page: number;
  not_treated_page: number;
  copay_claims_data_page: number;
  drug_id: string;
  region?: string;
  territory?: string;
  state?: string;
  cas?: string;
  rsd?: string;
  section:
    | "coverage_available"
    | "action_required"
    | "pending"
    | "specialty_pharmacy"
    | "copay_claims";
};

export enum PatientFilterStateActions {
  SET_QUERY_PARAMS = "setQueryParams",
  SET_REF_USER = "setRefUser",

  CHANGE_TREATABLE_PAGE = "changeTreatablePage",
  CHANGE_TODO_PAGE = "changeTodoPage",
  CHANGE_SP_TREATMENT_PAGE = "changeSPTreatmentPage",
  CHANGE_NOT_TREATED_PAGE = "changeNotTreatedPage",
  CHANGE_COPAY_CLAIM = "changeCopayClaims",
  CHANGE_USER_ROLE_AND_ID = "changeUserRoleAndId",
}

export type ActionStatisticsDashboardActionType =
  | {
      type: PatientFilterStateActions.SET_QUERY_PARAMS;
      payload: Omit<
        Omit<
          Omit<Omit<PatientFilterState, "treatable_page">, "todo_page">,
          "sp_treatment_page"
        >,
        "not_treated_page"
      >;
    }
  | {
      type: PatientFilterStateActions.SET_REF_USER;
      payload: string;
    }
  | {
      type: PatientFilterStateActions.CHANGE_TREATABLE_PAGE;
      payload: number;
    }
  | {
      type: PatientFilterStateActions.CHANGE_TODO_PAGE;
      payload: number;
    }
  | {
      type: PatientFilterStateActions.CHANGE_SP_TREATMENT_PAGE;
      payload: number;
    }
  | {
      type: PatientFilterStateActions.CHANGE_NOT_TREATED_PAGE;
      payload: number;
    }
  | {
      type: PatientFilterStateActions.CHANGE_COPAY_CLAIM;
      payload: number;
    }
  | {
      type: PatientFilterStateActions.CHANGE_USER_ROLE_AND_ID;
      payload: { role: string; id: string; user_ref: string };
    };

export type on_therapy = {
  patient_name: string;
  patient_id: number;
  bi_case_id: number;
  insurance_type: string;
  provider: string;
  treatment_status: string;
  date_of_treatment: null | string;
  date_of_next_treatment: null | string;
  treated_popup: boolean;
  prescription_id: number;
  payer: string;
};

export type off_therapy = {
  patient_name: string;
  patient_id: number;
  bi_case_id: number;
  insurance_type: string;
  provider: string;
  treatment_status: string;
  color_idx: 1 | 2 | 3 | 4 | 5;
  reason_for_not_treating_patient: null | string;
  bi_completed: null | string;
  treated_popup: boolean;
  prescription_id: number;
  payer: string;
};

export type pending_therapy = {
  patient_name: string;
  patient_id: number;
  bi_case_id: number;
  insurance_type?: string;
  payer?: string;
  provider: string;
  treatment_status: string;
  color_idx?: 1 | 2 | 3 | 4 | 5;
  treatment_status_color?: 1 | 2 | 3 | 4 | 5;
  bi_submitted: null | string;
  bi_completed: null | string;
  indiction?: string;
  note_text?: string;
  confirm_indication: {
    clickable: boolean;
    text: string;
  };
  case_type?: string;
  treated_popup: boolean;
  prescription_id: number;
  reverification: string;
};

export type PatientDashboardApiResponse = {
  code: number;
  message: string;
  data: {
    on_treatment: {
      on_therapy: on_therapy[];
      page_count: number;
    };
    off_treatment: {
      off_therapy: off_therapy[];
      page_count: number;
    };
    pending_treatment: {
      pending_therapy: pending_therapy[];
      page_count: number;
    };
    doctor_dropdown: { id: number; name: string }[];
    practice_dropdown: { id: number; name: string }[];
  };
};

export type ManufactureNextStepActionProps = {
  treatment_decision: boolean;
  date_of_treatment: boolean;
  no_action_required: boolean;
  precert_buttons: precertType[];
  update_buttons: updateType[];
  review_buttons: reviewType[];
  rx_pa_buttons: rxPAType[];
  update_pa_buttons: updatePAType[];
  review_pa_buttons: reviewPAType[];
  appeal_buttons: appealType[];
  copay_claim: boolean;
};
export type TreatableProps = {
  active_precert_id: number | null;
  appeal_precert_id: number | null;
  patient_name: string;
  patient_id: number;
  bi_case_id: number;
  reverification: string;
  bi_completed: string;
  payer: string;
  physician: string;
  medical_coverage_status: string;
  medical_coverage_status_color: number;
  medical_coverage_status_title: string;
  medical_next_steps: string;
  pharmacy_coverage_status: string;
  pharmacy_coverage_status_color: number;
  pharmacy_coverage_status_title: string;
  pharmacy_next_steps: string;
  case_type: string;
  patient_Initials: string;
  patient_DOB: string;
  pending_precert_id: number | null;
  prescription_id: number;
  insurance_type: string;
  drug_name: string;
  copay_status: string;
  practice: practiceProps;
  review_type: string;
  product_name: string;
  region: string;
  territory: string;
  state: string;
  original_id:string;
   next_steps: {
    next_step_verbiage: string;
  };
  next_step_info: string;
  received_at: string;
  qty:string;
  refills:string;
  pa_required:string;
  released:string;
  // drug_name: string;
  // copay_status: string;
  // next_steps: ManufactureNextStepActionProps;
  auto_transfer: boolean;
  patient_initials_dob:string;
};
export type CoverageAvailableProps = {
  treatables_data: TreatableProps[];
  page_count: number;
  dpn_count: number;
};
export type NotTreatedData = {
  patient_name: string;
  patient_id: number;
  bi_case_id: number;
  reverification: string | boolean;
  bi_submitted: string | null;
  bi_completed: string | null;
  insurance_type: string | null;
  provider: string;
  medical_coverage_status: string;
  medical_coverage_status_color: 1 | 2 | 3 | 4 | 5;
  medical_coverage_status_title: string;
  not_treated_button: boolean;
  pharmacy_coverage_status: string;
  pharmacy_coverage_status_color: 1 | 2 | 3 | 4 | 5;
  pharmacy_coverage_status_title: string;
  not_treated_pharmacy: boolean;
  pharmacy_provide_treatment_status?: string[];
  payer?: string;
  physician?: string;
  indication: string;
  confirm_indication: {
    text: "";
    clickable: false;
  };
  case_type?: string;
  kam_fam_notes?: {
    text: string;
    clickable: boolean;
  };
  prescription_id: number;
  active_precert_id: number;
  pending_precert_id: number;
  appeal_precert_id: number;
  rx_pa_prior_auth_id: number;
  review_prior_auth_id: number;
  appeal_prior_auth_id: number;
  drug_name: string;
  copay_status: string;
  auto_transfer: boolean;
  practice: practiceProps;
  region: string;
  territory: string;
  state: string;
  original_id:string;
  next_steps: {
    next_step_verbiage: string;
  };
  patient_initials_dob:string;
    received_at: string;
  qty:string;
  refills:string;
  pa_required:string;
  pa_status:string;
  released:string

  review_type:string;
  clinical_received:string;
  lomn_received:string;
};
export type CopayClaimsData = {
  patient_name: string;
  patient_id: number;
  bi_case_id: number;
  reverification: string | boolean;
  bi_submitted: string | null;
  bi_completed: string | null;
  insurance_type: string | null;
  provider: string;
  medical_coverage_status: string;
  medical_coverage_status_color: 1 | 2 | 3 | 4 | 5;
  medical_coverage_status_title: string;
  not_treated_button: boolean;
  pharmacy_coverage_status: string;
  pharmacy_coverage_status_color: 1 | 2 | 3 | 4 | 5;
  pharmacy_coverage_status_title: string;
  not_treated_pharmacy: boolean;
  pharmacy_provide_treatment_status?: string[];
  payer?: string;
  physician?: string;
  indication: string;
  confirm_indication: {
    text: "";
    clickable: false;
  };
  case_type?: string;
  kam_fam_notes?: {
    text: string;
    clickable: boolean;
  };
  prescription_id: number;
  active_precert_id: number;
  pending_precert_id: number;
  appeal_precert_id: number;
  rx_pa_prior_auth_id: number;
  review_prior_auth_id: number;
  appeal_prior_auth_id: number;
  drug_name: string;
  copay_status: string;

  next_steps: {
    next_step_verbiage: string;
  };
  next_step_info: string;
  insurance_name: string;
  days_until_copay_claim_filed: number;
  date_of_service: string;
  practice_name: string;
  practice: practiceProps;
  high_pt_cost_share: string;
  submitted_by: string;
  // next_steps:ManufactureNextStepActionProps
};
export type PendingDataProps = {
  not_treateds_data: NotTreatedData[];
  page_count: number;
  dpn_count: number;
};
export type practiceProps = {
  name: string;
  phone_number: string;
  fax_number: string;
  address: string;
};
export type CopayDataProps = {
  copay_claims_data: CopayClaimsData[];
  page_count: number;
  dpn_count: number;
};
export type sp_treatments_data = {
  patient_name: string;
  patient_id: number;
  bi_case_id: number;
  provider: string;
  provider_treatment_status: string;
  sp_transferred_to: string;
  sp_transfered_to: string;
  date_transferred: string;
  original_id:string;
  status_update: {
    status: string;
    color: string;
  };
  date_shipped: string;
  carrier: string;
  tracking_number: string;
  pharmacy_sp_status: string;
  pharmacy_sp_status_color: number;
  provide_treatment_status: string[];
  indication: string;
  confirm_indication: {
    text: "";
    clickable: false;
  };
  kam_fam_notes: {
    text: string;
    clickable: boolean;
  };
  scheduled_tx_date: string;
  prescription_id: number;
  active_precert_id: number;
  pending_precert_id: number;
  appeal_precert_id: number;
  rx_pa_prior_auth_id: number;
  review_prior_auth_id: number;
  appeal_prior_auth_id: number;
  practice: practiceProps;
  clinical_received:string;
  lomn_received:string;

  // drug_name:string;
  // copay_status:string;

  // next_steps:ManufactureNextStepActionProps
  next_step_info: string;
  drug_name: string;
  copay_status: string;
  sp_status: string;
  sp_substatus: string;
  region: string;
  territory: string;
  state: string;
  next_steps: ManufactureNextStepActionProps;
  patient_initials_dob:string;
  dispense_refill:string;
  refill_number:	string
  fill_number: string;
  oop_cost:	string
  qty_transferred:number
  qty_dispensed:string	
    released:string
  qty:string;
  refills:string;
  review_type:string;
  insurance_type:string;
  payer:string;
  pending_shipment_date:string;
};
export type TodoResponseDataType = {
  coverage_available: CoverageAvailableProps;
  action_required: ActionRequiredProps;
  specialty_pharmacy: {
    sp_treatments_data: sp_treatments_data[];
    page_count: number;
    dpn_count: number;
  };
  pending: PendingDataProps;
  copay_claims: CopayDataProps;
  doctor_dropdown: { id: number; name: string }[];
  practice_dropdown: { id: number; name: string }[];
  region_dropdown: string[];
  territory_dropdown: string[];
  state_dropdown: string[];
  cas_dropdown: { id: number; name: string }[];
  rsd_dropdown: { id: number; name: string }[];
  response:string;
  report_name:string;
};
export type TodoResponseType = {
  code: number;
  message: string;
  data: TodoResponseDataType;
  
};
export type TerritoryDropdownResponseType = {
  code: number;
  message: string;
  data: string[];
};
export type CasDropdownResponseType = {
  code: number;
  message: string;
  data: { id: number; name: string }[];
};
export type TodoDataProps = {
  original_id:string;
  patient_name: string;
  patient_id: number;
  bi_case_id: number;
  reverification: string;
  bi_completed: string;
  insurance_type?: string;
  physician: string;
  review_type: string;
  medical_coverage_status: string;
  medical_coverage_status_color: 1 | 2 | 3 | 4 | 5;
  medical_coverage_status_title: string;
  treatment_next_steps: null | string[];
  pharmacy_coverage_status: string;
  pharmacy_coverage_status_color: 1 | 2 | 3 | 4 | 5;
  pharmacy_coverage_status_title: string;
  patient_next_steps: null | string[];
  payer?: string;
  medical_next_steps: string[];
  pharmacy_next_steps: string[];
  // next_steps: ["Treated", "Transferred", "Not Treated"];
  patient_actv_status: "";
  indication: string;
  confirm_indication: {
    text: "";
    clickable: false;
  };
  kam_fam_notes: {
    text: "";
    clickable: false;
  };
  scheduled_tx_date: "Schedule";
  case_type: string;
  prescription_id: number;
  active_precert_id: number;
  pending_precert_id: number;
  appeal_precert_id: number;
  rx_pa_prior_auth_id: number;
  review_prior_auth_id: number;
  appeal_prior_auth_id: number;
  drug_name: string;
  copay_status: string;
  auto_transfer: boolean;
  practice: practiceProps;
  region: string;
  territory: string;
  state: string;

  next_steps: {
    next_step_verbiage: string;
  };
  next_step_info: string;
  clinical_received:string;
  lomn_received:string;
  patient_initials_dob:string;
  received_at:string;
  qty:string;
  refills:string;
  pa_required:string;
 released:string
  missing_info: 0 | 1;
  missing_info_text: string | null;

  // next_steps: ManufactureNextStepActionProps;
};
export type PendingProps = {};
export type ActionRequiredProps = {
  todos_data: TodoDataProps[];
  page_count: number;
  dpn_count: number;
};
export type specialtyPharmacyProps = {
  sp_treatments_data: sp_treatments_data[];
  page_count: number;
  dpn_count: number;
};

export type ActionDashboardState = {
  treatable_page: number;
  todo_page: number;
  sp_treatment_page: number;
  not_treated_page: number;
};
export enum ActionDashboardStateActions {
  CHANGE_TODO_PAGE = "changeTodoPage",
  CHANGE_TREATABLE_PAGE = "changeTreatablePage",
  CHANGE_SP_TREATMENT_PAGE = "changeSPTreatmentPage",
  CHANGE_NOT_TREATED_PAGE = "changeNotTreatedPage",
}
export type ActionDashboardActionType = {
  type: ActionDashboardStateActions;
  payload: number;
};

export type CommonProps = {
  currentPage: number;
  onPageChange:
    | ((action: ActionDashboardActionType) => void)
    | ((action: ActionStatisticsDashboardActionType) => void);
  refreshListing: () => void;
};
export type ActionRequiredCompProps = CommonProps & {
  tableData: ActionRequiredProps | undefined | any;
};
export type CoverageAvailableCompProps = CommonProps & {
  tableData: CoverageAvailableProps | undefined | any;
};
export type SpecialityPharmacyCompProps = CommonProps & {
  tableData:
    | {
        sp_treatments_data: sp_treatments_data[];
        page_count: number;
      }
    | undefined
    | any;
};
export type PendingCompProps = CommonProps & {
  tableData: PendingDataProps | undefined | any;
};

export type CoverageAvailableTypeProps = {
  tableData: CoverageAvailableProps;
  refreshListing: () => void;
  currentPage: number;
  onPageChange: () => void;
  changeAction: string;
};
export type ActionRequiredTypeProps = {
  tableData: ActionRequiredProps;
  refreshListing: () => void;
  currentPage: number;
  onPageChange: () => void;
  changeAction: string;
};
export type SpecialityPharmacyTypeProps = {
  tableData: specialtyPharmacyProps;
  refreshListing: () => void;
  currentPage: number;
  onPageChange: () => void;
  changeAction: string;
};
export type PendingTypeProps = {
  tableData: PendingDataProps;
  refreshListing: () => void;
  currentPage: number;
  onPageChange: () => void;
  changeAction: string;
};
export type CopayTypeProps = {
  tableData: CopayDataProps;
  refreshListing: () => void;
  currentPage: number;
  onPageChange: () => void;
  changeAction: string;
};

export type AllClaimsTypeProps = {
  case_id: number;
  copay: string;
  days_left: number;
  patient_name: string;
  product: string;
  status: string;
  submitted_date: string;
  submitting_practice: string;
  next_step: boolean;
};

export type DashboardTabConfig = {
  key: string;
  label: string;
  description: string;
  Component: FC<any>;
  dataSelector: (data: any) => any;
  pageKey: keyof PatientFilterState;
  changeAction: string;
};

export type PharmacyTableProps = {
  tableData: specialtyPharmacyProps;
};
export type PharmacyTableTypeProps = {
  tableData: sp_treatments_data[];
  setNoteModal: React.Dispatch<
    React.SetStateAction<{
      isModalVisible: boolean;
      caseId: number;
    }>
  >;
  setRequestTreatmentModal: React.Dispatch<
    React.SetStateAction<{
      isModalVisible: boolean;
      caseId: number;
      prescriptionId: number;
      bentype: string;
    }>
  >;
  setCancelIndicationModal: React.Dispatch<
    React.SetStateAction<{
      isModalVisible: boolean;
      caseId: number;
      prescriptionId: number;
    }>
  >;
  setCancelTreatmentModal: React.Dispatch<
    React.SetStateAction<{
      isModalVisible: boolean;
      caseId: number;
      prescriptionId: number;
    }>
  >;
};
export type PharmacyTableaRowProps = {
  data: sp_treatments_data;
  index: number;
  setNoteModal: React.Dispatch<
    React.SetStateAction<{
      isModalVisible: boolean;
      caseId: number;
    }>
  >;
  setRequestTreatmentModal: React.Dispatch<
    React.SetStateAction<{
      isModalVisible: boolean;
      caseId: number;
      prescriptionId: number;
      bentype: string;
    }>
  >;
  setCancelIndicationModal: React.Dispatch<
    React.SetStateAction<{
      isModalVisible: boolean;
      caseId: number;
      prescriptionId: number;
    }>
  >;
  setCancelTreatmentModal: React.Dispatch<
    React.SetStateAction<{
      isModalVisible: boolean;
      caseId: number;
      prescriptionId: number;
    }>
  >;
};
export type ActionDashboardProps = {
  apiEndpoint: APIEndpoints;
  userListEndpoint: APIEndpoints;
  initialState: PatientFilterState;
  tabs: DashboardTabConfig[];
  FilterComponent: FC<any>;
  allowedRoles: string[];
};

export type precertType = {
  precert: boolean;
  precert_id: number;
  ins_name: string;
  precert_status: string;
  key: number;
};
export type reviewType = {
  review: boolean;
  precert_id: number;
  ins_name: string;
  precert_status: string;
  key: number;
};
export type updateType = {
  update: boolean;
  precert_id: number;
  ins_name: string;
  precert_status: string;
  key: number;
};
export type rxPAType = {
  rx_pa: boolean;
  key: number;
  prior_authorization_id: number;
  ins_name: string;
  prior_authorization_status: string;
};
export type reviewPAType = {
  review_pa: boolean;
  key: number;
  prior_authorization_id: number;
  ins_name: string;
  prior_authorization_status: string;
};
export type updatePAType = {
  update_pa: boolean;
  key: number;
  prior_authorization_id: number;
  ins_name: string;
  prior_authorization_status: string;
};
export type appealType = {
  appeal: boolean;
  key: number;
  precert_id: number;
  ins_name: string;
  precert_status: string;
};
